import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useProjectStore } from '@/stores/project-store';
import { Button } from '@/components/ui/button';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { ticketApi } from '@/lib/api/ticket/ticketApi';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  TicketListByStatus,
  NO_STATUS_ID,
  type StatusGroup
} from '@/components/tickets/TicketListByStatus';
import { TicketKanban } from '@/components/tickets/TicketKanban';
import type { Ticket as TicketType } from 'dico-types';

type ViewMode = 'list' | 'kanban';

export function TicketsPage() {
  const { id: workspaceId } = useParams<{ id: string }>();

  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const getStatuses = useWorkspaceStore((s) => s.getStatuses);
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const statuses = workspaceId ? getStatuses(workspaceId) : [];
  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);

  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const statusGroups: StatusGroup[] = [
    ...statuses,
    { id: NO_STATUS_ID, name: 'No status', color: undefined }
  ];

  const loadTickets = useCallback(async () => {
    if (!workspaceId) return;
    setLoadState('loading');
    try {
      const ticketsRes = await ticketApi.listTickets(workspaceId, { limit: 100 });
      setTickets(ticketsRes.data ?? []);
      setLoadState('loaded');
    } catch {
      setLoadState('error');
    }
  }, [workspaceId]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (loadState !== 'error') return;
    toast.error('Failed to load tickets. Check your connection and try again.');
  }, [loadState]);

  const handleStatusChange = useCallback(
    async (ticketId: string, newStatusId: string | null) => {
      if (!workspaceId) return;
      try {
        await ticketApi.updateTicket(workspaceId, ticketId, { statusId: newStatusId });
        setTickets((prev) =>
          prev.map((t) => (t.id === ticketId ? { ...t, statusId: newStatusId } : t))
        );
      } catch {
        // Optionally show toast; keep UI unchanged on error
      }
    },
    [workspaceId]
  );

  const filteredGroups = statusGroups;

  // Filter by project
  const filteredTickets = tickets.filter((t) => {
    // Filter by project if selectedProjectId is set
    if (selectedProjectId !== null) {
      if (t.projectId !== selectedProjectId) return false;
    }
    return true;
  });

  if (!workspace) {
    return (
      <div className="text-muted-foreground flex min-h-screen items-center justify-center">
        Workspace not found.
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="mx-auto flex h-12 w-full items-center justify-between border-b px-4">
        <h1 className="text-foreground text-sm font-medium tracking-tight">Tickets</h1>

        <div className="flex items-center gap-2">
          <div className="border-border bg-muted flex items-center rounded-lg border">
            <Button
              variant={viewMode === 'list' ? 'outline' : 'ghost'}
              size="icon-sm"
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <MonoIcon type="List" />
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'outline' : 'ghost'}
              size="icon-sm"
              onClick={() => setViewMode('kanban')}
              aria-label="Kanban view"
            >
              <MonoIcon type="SquareKanban" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={loadTickets}
            disabled={loadState === 'loading'}
            className="gap-1.5"
          >
            <MonoIcon type="RefreshCcw" className={cn(loadState === 'loading' && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="overflow-hidden">
        {loadState === 'loading' && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-24 rounded-lg" />
              <Skeleton className="h-9 w-24 rounded-lg" />
            </div>
          </div>
        )}

        {loadState === 'loaded' && tickets.length > 0 && filteredTickets.length === 0 && (
          <div className="border-border text-muted-foreground rounded-lg border border-dashed p-4 text-center text-sm">
            No tickets found for the selected project.
          </div>
        )}

        {loadState === 'loaded' && filteredGroups.length > 0 && viewMode === 'list' && (
          <div className="w-3xl mx-auto p-6">
            <TicketListByStatus
              tickets={filteredTickets}
              statusGroups={filteredGroups}
              workspaceId={workspaceId}
            />
          </div>
        )}
        {loadState === 'loaded' && filteredGroups.length > 0 && viewMode === 'kanban' && (
          <div className="">
            <TicketKanban
              tickets={filteredTickets}
              statusGroups={filteredGroups}
              workspaceId={workspaceId}
              onStatusChange={handleStatusChange}
            />
          </div>
        )}
      </main>
    </div>
  );
}
